import * as React from "react"
import { cn } from "@/lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  ChevronDown,
  Diamond
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

// Updated Card variants to match lift-ui/Figma design
const cardVariants = cva(
  "rounded-xl bg-white overflow-hidden", // Changed to rounded-xl (12px) to match Figma
  {
    variants: {
      variant: {
        default: "bg-white",
        dark: "bg-[#242424] text-white",
      },
      shadow: {
        none: "",
        border: "shadow-[0px_0px_0px_1px_rgba(46,43,41,0.10)]", // Border-like shadow
        soft: "shadow-[0px_2px_8px_-2px_rgba(46,43,41,0.10)]",   // Soft shadow
        medium: "shadow-[0px_8px_16px_-4px_rgba(46,43,41,0.10)]", // Medium shadow
        strong: "shadow-[0px_24px_40px_-8px_rgba(46,43,41,0.10)]", // Strong shadow
      },
    },
    defaultVariants: {
      variant: "default",
      shadow: "border",
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, shadow, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card"
      className={cn(cardVariants({ variant, shadow }), className)}
      {...props}
    />
  )
)
Card.displayName = "Card"

// Updated CardHeader component to use ChevronDown
const CardHeader = React.forwardRef<
  React.ElementRef<"div">,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'default' | 'dark';
    showIcon?: boolean;
    showMoreIcon?: boolean;
    icon?: React.ReactNode;
  }
>(({ className, variant = 'default', showIcon = true, showMoreIcon = true, icon, children, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-header"
    className={cn(
      "flex justify-between items-center p-4",
      variant === 'dark' ? "bg-[#242424]" : "bg-white",
      "rounded-t-xl",
      className
    )}
    {...props}
  >
    <div className="flex-1 flex justify-start items-center gap-2">
      {showIcon && (
        icon || <Diamond className="w-4 h-4" />
      )}
      <div className={cn(
        "text-base font-semibold leading-[19.2px]",
        variant === 'dark' ? "text-[#FAF4F0]" : "text-[#242424]"
      )}>
        {children}
      </div>
    </div>
    {showMoreIcon && (
      <ChevronDown 
        className={cn(
          "w-[18px] h-[18px]",
          variant === 'dark' ? "text-[#A09D9B]" : "text-[#585755]"
        )} 
      />
    )}
  </div>
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    data-slot="card-title"
    className={cn(
      "text-base font-semibold leading-[19.2px] tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="card-description"
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'default' | 'dark';
    showPlaceholder?: boolean;
  }
>(({ className, variant = 'default', showPlaceholder = false, children, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-content"
    className={cn(
      "p-4 flex flex-col justify-start items-start gap-2",
      className
    )}
    {...props}
  >
    {showPlaceholder ? (
      <div className="w-full h-[110px] relative">
        <div className="w-full h-[110px] bg-[#F7F4F2] rounded-lg border border-[#CCC8C6]" />
      </div>
    ) : children}
  </div>
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  React.ElementRef<"div">,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'default' | 'dark';
    buttons?: Array<{
      label: string;
      variant?: 'primary' | 'secondary' | 'tertiary' | 'tertiaryDark';
      onClick?: () => void;
    }>;
  }
>(({ className, variant = 'default', buttons = [], children, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-footer"
    className={cn(
      "flex justify-end items-center gap-2 p-4",
      variant === 'dark' ? "bg-[#242424]" : "bg-white",
      "rounded-b-xl",
      className
    )}
    {...props}
  >
    {buttons.length > 0 ? (
      buttons.map((button, index) => (
        <button
          key={index}
          className={cn(
            "px-[10px] py-2 rounded-lg flex justify-center items-center gap-1 text-sm font-semibold leading-[18.2px] transition-colors",
            variant === 'dark' && button.variant === 'tertiary' 
              ? "bg-[#242424] text-[#FAF4F0] hover:bg-[#333333]"
              : button.variant === 'primary' 
                ? "bg-[#DAF565] text-[#242424] hover:bg-[#D1F055]"
                : button.variant === 'secondary'
                  ? "bg-[#E5E1DF] text-[#242424] hover:bg-[#DDD9D7]"
                  : button.variant === 'tertiary'
                    ? "bg-white text-[#242424] hover:bg-[#F5F5F5]"
                    : "bg-[#E5E1DF] text-[#242424] hover:bg-[#DDD9D7]"
          )}
          onClick={button.onClick}
        >
          {button.label}
        </button>
      ))
    ) : children}
  </div>
))
CardFooter.displayName = "CardFooter"

// Button variants to match Figma
const buttonVariants = cva(
  "px-[10px] py-2 rounded-lg flex justify-center items-center gap-1 text-sm font-semibold leading-[18.2px] transition-colors",
  {
    variants: {
      variant: {
        primary: "bg-[#DAF565] text-[#242424] hover:bg-[#D1F055]",
        secondary: "bg-[#E5E1DF] text-[#242424] hover:bg-[#DDD9D7]",
        tertiary: "bg-white text-[#242424] hover:bg-[#F5F5F5]",
        tertiaryDark: "bg-[#242424] text-[#FAF4F0] hover:bg-[#333333]",
      },
    },
    defaultVariants: {
      variant: "secondary",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const CardButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant }), className)}
      {...props}
    />
  )
)
CardButton.displayName = "CardButton"

// Types for DynamicCard
export type TrendDirection = 'up' | 'down' | 'neutral';
export type ValueType = 'currency' | 'number' | 'percentage' | 'text' | 'custom';

export interface TrendConfig {
  direction: TrendDirection;
  value: string | number;
  label?: string;
  showIcon?: boolean;
  colorScheme?: 'default' | 'success' | 'warning' | 'destructive';
}

export interface CardAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'tertiaryDark';
  disabled?: boolean;
}

export interface BadgeConfig {
  text: string;
  variant?: 'beige' | 'pink' | 'orange' | 'electricGreen' | 'blue' | 'green' | 'teal' | 'lavender' | 
            'success' | 'warning' | 'danger' | 'disabled' | 'default' | 'secondary' | 'destructive' | 'outline';
  className?: string;
}

export interface ProgressConfig {
  value: number;
  max?: number;
  label?: string;
  showPercentage?: boolean;
}

export interface DynamicCardProps {
  /** Visual variant */
  variant?: 'default' | 'dark';
  /** Shadow variant to match Figma */
  shadow?: 'none' | 'border' | 'soft' | 'medium' | 'strong';
  /** Card size */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Layout orientation */
  layout?: 'vertical' | 'horizontal' | 'compact';
  /** Custom sections */
  sections?: Array<{ type: 'header' | 'content' | 'footer' | 'custom'; content?: React.ReactNode; className?: string }>;

  title?: string;
  subtitle?: string;
  description?: string;
  value?: string | number;
  valueType?: ValueType;
  valuePrefix?: string;
  valueSuffix?: string;
  valueFormatter?: (value: string | number) => string;
  trend?: TrendConfig;
  className?: string;
  actions?: CardAction[];
  badges?: BadgeConfig[];
  clickable?: boolean;
  onClick?: () => void;
  progress?: ProgressConfig;
  customContent?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  selected?: boolean;
  
  // Header props
  showIcon?: boolean;
  showMoreIcon?: boolean;
  headerIcon?: React.ReactNode;
  
  // Content props
  showPlaceholder?: boolean;
}

// Utility functions
const formatValue = (
  value: string | number,
  type: ValueType,
  prefix?: string,
  suffix?: string,
  formatter?: (value: string | number) => string
): string => {
  if (formatter) return formatter(value);
  
  const stringValue = value.toString();
  const prefixStr = prefix || '';
  const suffixStr = suffix || '';
  
  switch (type) {
    case 'currency':
      const numValue = typeof value === 'number' ? value : parseFloat(stringValue);
      return `${prefixStr}$${numValue.toLocaleString()}${suffixStr}`;
    case 'number':
      const num = typeof value === 'number' ? value : parseFloat(stringValue);
      return `${prefixStr}${num.toLocaleString()}${suffixStr}`;
    case 'percentage':
      return `${prefixStr}${stringValue}%${suffixStr}`;
    case 'text':
      return `${prefixStr}${stringValue}${suffixStr}`;
    case 'custom':
      return stringValue;
    default:
      return `${prefixStr}${stringValue}${suffixStr}`;
  }
};

const getTrendIcon = (direction: TrendDirection, showIcon: boolean = true) => {
  if (!showIcon) return null;
  
  switch (direction) {
    case 'up':
      return <TrendingUp className="h-4 w-4" />;
    case 'down':
      return <TrendingDown className="h-4 w-4" />;
    case 'neutral':
      return <Minus className="h-4 w-4" />;
    default:
      return null;
  }
};

const getTrendColor = (direction: TrendDirection, colorScheme?: string) => {
  if (colorScheme) {
    switch (colorScheme) {
      case 'success':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'destructive':
        return 'text-red-600';
      default:
        return 'text-muted-foreground';
    }
  }
  
  switch (direction) {
    case 'up':
      return 'text-green-600';
    case 'down':
      return 'text-red-600';
    case 'neutral':
      return 'text-muted-foreground';
    default:
      return 'text-muted-foreground';
  }
};

// Map size to padding classes
const sizeClasses: Record<Exclude<DynamicCardProps['size'], undefined>, string> = {
  sm: 'text-sm',
  md: '',
  lg: 'text-lg',
  xl: 'text-xl',
};

// Enhanced DynamicCard component
export const DynamicCard: React.FC<DynamicCardProps> = ({
  variant = 'default',
  shadow = 'border',
  size = 'md',
  layout = 'vertical',
  sections = [],
  title,
  subtitle,
  description,
  value,
  valueType = 'text',
  valuePrefix,
  valueSuffix,
  valueFormatter,
  trend,
  className = '',
  actions = [],
  badges = [],
  clickable = false,
  onClick,
  progress,
  customContent,
  loading = false,
  disabled = false,
  selected = false,
  showIcon = true,
  showMoreIcon = true,
  headerIcon,
  showPlaceholder = false,
}) => {
  // Compose root Card classes
  const cardClasses = cn(
    clickable && 'cursor-pointer hover:shadow-soft transition-shadow',
    selected && 'ring-2 ring-primary',
    disabled && 'opacity-50 pointer-events-none',
    className
  );

  const handleCardClick = () => {
    if (clickable && onClick && !disabled) {
      onClick();
    }
  };

  const renderValue = () => {
    if (!value && value !== 0) return null;
    
    const formattedValue = formatValue(value, valueType, valuePrefix, valueSuffix, valueFormatter);
    
    return (
      <div className="text-2xl font-bold">
        {formattedValue}
      </div>
    );
  };

  const renderTrend = () => {
    if (!trend) return null;
    
    const trendColor = getTrendColor(trend.direction, trend.colorScheme);
    const trendIcon = getTrendIcon(trend.direction, trend.showIcon);
    
    return (
      <div className={`flex items-center space-x-1 text-sm ${trendColor}`}>
        {trendIcon}
        <span className="font-medium">
          {typeof trend.value === 'number' ? trend.value.toLocaleString() : trend.value}
        </span>
        {trend.label && <span className="text-muted-foreground">{trend.label}</span>}
      </div>
    );
  };

  const renderBadges = () => {
    if (badges.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-1">
        {badges.map((badge, index) => (
          <Badge
            key={index}
            variant={badge.variant || 'default'}
            className={badge.className}
          >
            {badge.text}
          </Badge>
        ))}
      </div>
    );
  };

  const renderProgress = () => {
    if (!progress) return null;
    
    return (
      <div className="space-y-2">
        {progress.label && (
          <div className="flex items-center justify-between gap-2 text-sm">
            <span>{progress.label}</span>
            {progress.showPercentage && (
              <span className="text-muted-foreground">
                {Math.round((progress.value / (progress.max || 100)) * 100)}%
              </span>
            )}
          </div>
        )}
        <Progress value={progress.value} max={progress.max || 100} />
      </div>
    );
  };

  if (loading) {
    return (
      <Card variant={variant} shadow={shadow} className={cardClasses}>
        <CardContent className="flex items-center justify-center h-32">
          <div className="animate-pulse space-y-2 w-full">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-6 bg-muted rounded w-1/2"></div>
            <div className="h-3 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      variant={variant} 
      shadow={shadow} 
      className={cardClasses} 
      onClick={handleCardClick}
    >
      {sections && sections.length > 0 ? (
        sections.map((section, index) => {
          const { type, content, className: secClassName } = section;
          switch (type) {
            case 'header':
              return (
                <CardHeader 
                  key={index} 
                  className={secClassName} 
                  variant={variant}
                  showIcon={showIcon}
                  showMoreIcon={showMoreIcon}
                  icon={headerIcon}
                >
                  {content}
                </CardHeader>
              );
            case 'content':
              return (
                <CardContent key={index} className={secClassName} variant={variant}>
                  {content}
                </CardContent>
              );
            case 'footer':
              return (
                <CardFooter key={index} className={secClassName} variant={variant}>
                  {content}
                </CardFooter>
              );
            default:
              return <React.Fragment key={index}>{content}</React.Fragment>;
          }
        })
      ) : (
        <>
          {title && (
            <CardHeader 
              variant={variant} 
              showIcon={showIcon} 
              showMoreIcon={showMoreIcon}
              icon={headerIcon}
            >
              {title}
            </CardHeader>
          )}
          
          <CardContent variant={variant} showPlaceholder={showPlaceholder}>
            {!showPlaceholder && (
              <>
                {renderValue()}
                {renderTrend()}
                {renderProgress()}
                {renderBadges()}
                {customContent}
                {description && (
                  <p className="text-sm text-muted-foreground pt-2">
                    {description}
                  </p>
                )}
              </>
            )}
          </CardContent>
          
          {actions.length > 0 && (
            <CardFooter 
              variant={variant}
              buttons={actions.map(action => ({
                label: action.label,
                variant: action.variant,
                onClick: action.onClick,
              }))}
            />
          )}
        </>
      )}
    </Card>
  );
};

// Pre-built card variants with updated styling
export const KPICard: React.FC<{
  title: string;
  value: string | number;
  trend?: TrendConfig;
  shadow?: 'none' | 'border' | 'soft' | 'medium' | 'strong';
  variant?: 'default' | 'dark';
  className?: string;
}> = ({ title, value, trend, shadow = 'border', variant = 'default', className }) => (
  <DynamicCard
    title={title}
    value={value}
    valueType="text"
    trend={trend}
    shadow={shadow}
    variant={variant}
    className={className}
    size='sm'
  />
);

// Pre-built metric card
export const MetricCard: React.FC<{
  title: string;
  value: number;
  valueType?: ValueType;
  trend?: TrendConfig;
  progress?: ProgressConfig;
  shadow?: 'none' | 'border' | 'soft' | 'medium' | 'strong';
  variant?: 'default' | 'dark';
  className?: string;
}> = ({ title, value, valueType = 'number', trend, progress, shadow = 'border', variant = 'default', className }) => (
  <DynamicCard
    title={title}
    value={value}
    valueType={valueType}
    trend={trend}
    progress={progress}
    shadow={shadow}
    variant={variant}
    className={className}
  />
);

// Pre-built action card
export const ActionCard: React.FC<{
  title: string;
  description?: string;
  actions: CardAction[];
  badges?: BadgeConfig[];
  shadow?: 'none' | 'border' | 'soft' | 'medium' | 'strong';
  variant?: 'default' | 'dark';
  className?: string;
}> = ({ title, description, actions, badges, shadow = 'border', variant = 'default', className }) => (
  <DynamicCard
    title={title}
    description={description}
    actions={actions}
    badges={badges}
    shadow={shadow}
    variant={variant}
    className={className}
  />
);

export { 
  Card, 
  CardHeader, 
  CardFooter, 
  CardTitle, 
  CardDescription, 
  CardContent, 
  cardVariants, 
  buttonVariants, 
  CardButton,
}
// DynamicCard and related components/types are already exported above with their declarations
